/**
 * Paramètres de recherche, de calcul et d'affichage — annexe B, EF-PAR-001.
 *
 * Les contraintes énoncées ici sont normatives : toute valeur hors contrainte
 * rend un fichier de paramètres invalide dans sa totalité (EF-PAR-003), et
 * empêche le calcul en affichant l'erreur au niveau du champ fautif (§17).
 */

import { DEFAULT_ENABLED_CATEGORIES, isCategoryKey, type CategoryKey } from './categories';
import { fromFields, type LocalDateTime } from './local-datetime';

export interface SearchParameters {
  readonly categories: readonly CategoryKey[];
  readonly corridorMeters: number;
  readonly skipStartMeters: number;
  readonly skipFinishMeters: number;
  readonly budgetSeconds: number;
}

export interface SpeedParameters {
  readonly referenceKmh: number;
  readonly uphill5Kmh: number;
  readonly maxKmh: number;
  readonly minKmh: number;
}

export interface PauseParameters {
  readonly enabled: boolean;
  readonly intervalSeconds: number;
  readonly durationSeconds: number;
}

export interface SleepParameters {
  readonly enabled: boolean;
  /** Heure de coucher exprimée en secondes depuis minuit. */
  readonly bedTimeSecondsOfDay: number;
  readonly durationSeconds: number;
  readonly declinedByUser: boolean;
}

export interface ScheduleParameters {
  readonly startLocal: LocalDateTime;
  readonly pauses: PauseParameters;
  readonly sleep: SleepParameters;
}

export interface DisplayParameters {
  readonly hiddenCategories: readonly CategoryKey[];
  readonly pinnedOnly: boolean;
  readonly excludedOnly: boolean;
  readonly groupingThresholdMeters: number;
}

export interface DeviceParameters {
  readonly keepScreenAwake: boolean;
}

export interface Parameters {
  readonly search: SearchParameters;
  readonly speed: SpeedParameters;
  readonly schedule: ScheduleParameters;
  readonly display: DisplayParameters;
  readonly device: DeviceParameters;
}

/** Bornes normatives, exposées pour l'interface et pour la validation. */
export const LIMITS = {
  corridorMeters: { min: 100, max: 5000, step: 100 },
  budgetSeconds: { min: 60, max: 1800 },
  referenceKmh: { min: 5, max: 60 },
  uphill5Kmh: { min: 4, max: 60 },
  maxKmh: { min: 10, max: 90 },
  minKmh: { min: 3, max: 20 },
  pauseIntervalSeconds: { min: 1800, max: 43200 },
  pauseDurationSeconds: { min: 300, max: 7200 },
  sleepDurationSeconds: { min: 3600, max: 43200 },
  groupingThresholdMeters: { min: 0, max: 5000, step: 100 },
  noteCodePoints: 500,
  titleCodePoints: 100,
  manualNameCodePoints: 200,
} as const;

/**
 * Borne supérieure de la vitesse maximale saisissable. Sert de plafond à la
 * fenêtre de projection de EF-CAL-007, indépendamment de tout réglage.
 */
export const SPEED_CEILING_KMH = LIMITS.maxKmh.max;

export function defaultParameters(startLocal?: LocalDateTime): Parameters {
  return {
    search: {
      categories: [...DEFAULT_ENABLED_CATEGORIES].filter((key) => key !== 'checkpoint'),
      corridorMeters: 500,
      skipStartMeters: 0,
      skipFinishMeters: 0,
      budgetSeconds: 600,
    },
    speed: { referenceKmh: 25, uphill5Kmh: 10, maxKmh: 40, minKmh: 5 },
    schedule: {
      startLocal: startLocal ?? fromFields({ year: 2026, month: 1, day: 1, hour: 6, minute: 0, second: 0 }),
      pauses: { enabled: false, intervalSeconds: 14400, durationSeconds: 1800 },
      sleep: { enabled: false, bedTimeSecondsOfDay: 23 * 3600, durationSeconds: 21600, declinedByUser: false },
    },
    display: {
      hiddenCategories: [],
      pinnedOnly: false,
      excludedOnly: false,
      groupingThresholdMeters: 1000,
    },
    device: { keepScreenAwake: false },
  };
}

/** Champ fautif et cause, affichés au niveau du champ concerné. */
export interface FieldIssue {
  readonly field: string;
  readonly message: string;
}

export interface ValidationOutcome {
  readonly issues: readonly FieldIssue[];
  /** Avertissements non bloquants : le calcul reste possible. */
  readonly warnings: readonly FieldIssue[];
}

const inRange = (value: number, min: number, max: number): boolean =>
  Number.isFinite(value) && value >= min && value <= max;

/**
 * Valide les quatre vitesses de EF-CAL-001, intervalles et contraintes
 * croisées comprises.
 */
export function validateSpeed(speed: SpeedParameters): ValidationOutcome {
  const issues: FieldIssue[] = [];
  const warnings: FieldIssue[] = [];

  if (!inRange(speed.referenceKmh, LIMITS.referenceKmh.min, LIMITS.referenceKmh.max)) {
    issues.push({
      field: 'speed.referenceKmh',
      message: `La vitesse sur le plat doit être comprise entre ${LIMITS.referenceKmh.min} et ${LIMITS.referenceKmh.max} km/h.`,
    });
  }
  if (!inRange(speed.uphill5Kmh, LIMITS.uphill5Kmh.min, LIMITS.uphill5Kmh.max)) {
    issues.push({
      field: 'speed.uphill5Kmh',
      message: `La vitesse à +5 % doit être comprise entre ${LIMITS.uphill5Kmh.min} et ${LIMITS.uphill5Kmh.max} km/h.`,
    });
  }
  if (!inRange(speed.maxKmh, LIMITS.maxKmh.min, LIMITS.maxKmh.max)) {
    issues.push({
      field: 'speed.maxKmh',
      message: `La vitesse maximale doit être comprise entre ${LIMITS.maxKmh.min} et ${LIMITS.maxKmh.max} km/h.`,
    });
  }
  if (!inRange(speed.minKmh, LIMITS.minKmh.min, LIMITS.minKmh.max)) {
    issues.push({
      field: 'speed.minKmh',
      message: `La vitesse minimale doit être comprise entre ${LIMITS.minKmh.min} et ${LIMITS.minKmh.max} km/h.`,
    });
  }
  if (issues.length > 0) return { issues, warnings };

  if (!(speed.minKmh < speed.maxKmh)) {
    issues.push({
      field: 'speed.minKmh',
      message: 'La vitesse minimale doit être strictement inférieure à la vitesse maximale.',
    });
  }
  if (!(speed.uphill5Kmh <= speed.referenceKmh)) {
    issues.push({
      field: 'speed.uphill5Kmh',
      message: 'La vitesse à +5 % ne peut pas dépasser la vitesse sur le plat.',
    });
  }
  if (!(speed.minKmh <= speed.uphill5Kmh)) {
    issues.push({
      field: 'speed.minKmh',
      message: 'La vitesse minimale ne peut pas dépasser la vitesse à +5 %.',
    });
  }
  if (!(speed.referenceKmh <= speed.maxKmh)) {
    issues.push({
      field: 'speed.referenceKmh',
      message: 'La vitesse sur le plat ne peut pas dépasser la vitesse maximale.',
    });
  }

  // L'égalité est autorisée mais produit un avertissement non bloquant.
  if (issues.length === 0 && speed.uphill5Kmh === speed.referenceKmh) {
    warnings.push({
      field: 'speed.uphill5Kmh',
      message: 'Le profil est constant : la pente n’influe pas sur la durée estimée.',
    });
  }

  return { issues, warnings };
}

/** Valide les paramètres de recherche. La longueur du parcours borne la portion. */
export function validateSearch(search: SearchParameters, routeLengthMeters: number | null): ValidationOutcome {
  const issues: FieldIssue[] = [];
  const warnings: FieldIssue[] = [];

  if (
    !inRange(search.corridorMeters, LIMITS.corridorMeters.min, LIMITS.corridorMeters.max) ||
    search.corridorMeters % LIMITS.corridorMeters.step !== 0
  ) {
    issues.push({
      field: 'search.corridorMeters',
      message: 'Le corridor doit être compris entre 0,1 km et 5 km, par pas de 0,1 km.',
    });
  }
  if (!Number.isFinite(search.skipStartMeters) || search.skipStartMeters < 0) {
    issues.push({ field: 'search.skipStartMeters', message: 'La portion ignorée au départ ne peut pas être négative.' });
  }
  if (!Number.isFinite(search.skipFinishMeters) || search.skipFinishMeters < 0) {
    issues.push({ field: 'search.skipFinishMeters', message: "La portion ignorée à l'arrivée ne peut pas être négative." });
  }
  if (
    routeLengthMeters !== null &&
    search.skipStartMeters >= 0 &&
    search.skipFinishMeters >= 0 &&
    search.skipStartMeters + search.skipFinishMeters >= routeLengthMeters
  ) {
    issues.push({
      field: 'search.skipFinishMeters',
      message: 'La somme des portions ignorées doit rester inférieure à la longueur du parcours.',
    });
  }
  if (!inRange(search.budgetSeconds, LIMITS.budgetSeconds.min, LIMITS.budgetSeconds.max)) {
    issues.push({
      field: 'search.budgetSeconds',
      message: 'Le budget de recherche doit être compris entre 1 et 30 minutes.',
    });
  }
  for (const key of search.categories) {
    if (!isCategoryKey(key) || key === 'checkpoint') {
      issues.push({ field: 'search.categories', message: `Catégorie de recherche inconnue : ${String(key)}.` });
      break;
    }
  }
  if (search.categories.length === 0) {
    warnings.push({ field: 'search.categories', message: 'Aucune catégorie active : la recherche ne renverra rien.' });
  }

  return { issues, warnings };
}

/** Valide les paramètres d'arrêt (EF-CAL-003, EF-CAL-004). */
export function validateSchedule(schedule: ScheduleParameters): ValidationOutcome {
  const issues: FieldIssue[] = [];
  const warnings: FieldIssue[] = [];

  if (schedule.pauses.enabled) {
    if (!inRange(schedule.pauses.intervalSeconds, LIMITS.pauseIntervalSeconds.min, LIMITS.pauseIntervalSeconds.max)) {
      issues.push({
        field: 'schedule.pauses.intervalSeconds',
        message: "L'intervalle de pédalage doit être compris entre 30 minutes et 12 heures.",
      });
    }
    if (!inRange(schedule.pauses.durationSeconds, LIMITS.pauseDurationSeconds.min, LIMITS.pauseDurationSeconds.max)) {
      issues.push({
        field: 'schedule.pauses.durationSeconds',
        message: 'La durée de pause doit être comprise entre 5 minutes et 2 heures.',
      });
    }
    if (issues.length === 0 && schedule.pauses.durationSeconds >= schedule.pauses.intervalSeconds) {
      warnings.push({
        field: 'schedule.pauses.durationSeconds',
        message: 'Le temps d’arrêt dépassera le temps de roulage.',
      });
    }
  }

  if (schedule.sleep.enabled) {
    if (!inRange(schedule.sleep.durationSeconds, LIMITS.sleepDurationSeconds.min, LIMITS.sleepDurationSeconds.max)) {
      issues.push({
        field: 'schedule.sleep.durationSeconds',
        message: 'La durée de sommeil doit être comprise entre 1 et 12 heures.',
      });
    }
    if (!inRange(schedule.sleep.bedTimeSecondsOfDay, 0, 86399)) {
      issues.push({ field: 'schedule.sleep.bedTimeLocal', message: "L'heure de coucher est invalide." });
    }
  }

  return { issues, warnings };
}

export function validateDisplay(display: DisplayParameters): ValidationOutcome {
  const issues: FieldIssue[] = [];
  if (
    !inRange(display.groupingThresholdMeters, LIMITS.groupingThresholdMeters.min, LIMITS.groupingThresholdMeters.max) ||
    display.groupingThresholdMeters % LIMITS.groupingThresholdMeters.step !== 0
  ) {
    issues.push({
      field: 'display.groupingThresholdMeters',
      message: 'Le seuil de regroupement doit être compris entre 0 et 5 km, par pas de 0,1 km.',
    });
  }
  for (const key of display.hiddenCategories) {
    if (!isCategoryKey(key)) {
      issues.push({ field: 'display.hiddenCategories', message: `Catégorie inconnue : ${String(key)}.` });
      break;
    }
  }
  return { issues, warnings: [] };
}

/** Valide la totalité des paramètres. */
export function validateParameters(
  parameters: Parameters,
  routeLengthMeters: number | null,
): ValidationOutcome {
  const parts = [
    validateSearch(parameters.search, routeLengthMeters),
    validateSpeed(parameters.speed),
    validateSchedule(parameters.schedule),
    validateDisplay(parameters.display),
  ];
  return {
    issues: parts.flatMap((p) => p.issues),
    warnings: parts.flatMap((p) => p.warnings),
  };
}

/** Paramètres nécessaires au calcul, au sens de l'empreinte de EF-CAL-005. */
export type ComputationParameters = Pick<Parameters, 'speed' | 'schedule'>;
